// React imports
import React, { useState, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom';

// Generic Import
import dayjs from 'dayjs';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import _ from 'lodash';

// MUI imports
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { red } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faCloudArrowUp, faXmark, faPen } from '@fortawesome/free-solid-svg-icons';
import { faFileJpg, faFilePdf, faFileWord, faFileExcel, faFilePowerpoint, faFile } from '@fortawesome/pro-light-svg-icons';

// RTF Editor
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import { MenuButtonBold, MenuButtonItalic, MenuControlsContainer, MenuDivider, MenuSelectHeading, RichTextEditor, type RichTextEditorRef } from 'mui-tiptap';

// Local imports
import TipTapMentionSuggestion, { mentionItemsFromOrg } from '../components/TipTapMentionSuggestion.js';
import { renderMentions } from '../../utils/mentions.js';
import { lostReasonText } from '../../../schema/lostReasons.js';

function SidePaneFilesAndComments(props) {
    const { me, organization, opportunity, accesstoken, onUpdateOpportunity } = props;

    const updates = opportunity.updates;
    const [ error, setError ] = useState(false);

    const [ showRichText, setShowRichText ] = useState(false);
    const [ onPosting, setOnPosting ] = useState(false);
    const [ file, setFile ] = useState(null);
    const [ files, setFiles ] = useState([]);
    const [ deleting, setDeleting ] = useState(null);
    const [ editing, setEditing ] = useState(null);
    const editRteRef = useRef(null);
    const theme = useTheme();

    const lastHash = useRef('');
    const location = useLocation();

    // Draft persistence via sessionStorage
    const draftKey = `comment-draft-${opportunity?.id}`;
    const savedDraft = sessionStorage.getItem(draftKey);

    useEffect(() => {
        if (savedDraft && !showRichText) {
            setShowRichText(true);
            const func = () => {
                if (rteRef.current?.editor) {
                    rteRef.current.editor.commands.setContent(savedDraft);
                } else {
                    setTimeout(func, 10);
                }
            };
            func();
        }
    }, []);

    useEffect(() => {
        if (location.hash) {
            lastHash.current = location.hash.slice(1); // safe hash for further use after navigation
        }

        setTimeout(() => {
            if (lastHash.current && document.getElementById(lastHash.current)) {
                document
                    .getElementById(lastHash.current)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                lastHash.current = '';
            }
        }, 100);
    }, [location, updates]);

    const setFileRef = useRef();
    setFileRef.current = setFile;
    const setFilesRef = useRef();
    setFilesRef.current = setFiles;
    const rteRef = useRef(null);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: async (acceptedFiles) => {
            setFileRef.current({ state: 'uploading' }); 

            const formData = new FormData();
            const file = acceptedFiles[0];
            formData.append('file', file, file.name);
            const { data } = await axios({
                url: `/api/opportunities/upload_file?organizationId=${organization.id}&opportunityId=${opportunity.id}&accesstoken=${accesstoken}`,
                method: 'post',
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            setFilesRef.current(f => [...f, { url: data.location, name: file.name }]);
            setFileRef.current(null);
        },
    });

    async function onPost() {
        const html = rteRef.current?.editor?.getHTML();
        if (html.length > 10 || !_.isEmpty(files)) {
            const newUpdates = [...updates || [], {
                created: new Date().getTime(),
                createdBy: me?.email,
                html,
                files,
            }];
            setOnPosting(true);
            if (await setUpdates({ updates: newUpdates })) {
                sessionStorage.removeItem(draftKey);
                setShowRichText(false);
                setFiles([]);
            }
            setOnPosting(false);
        }
    }

    async function setUpdates({ updates }) {
        setError(false);
        if (opportunity.status === 'draft') {
            onUpdateOpportunity({ opportunity: { ...opportunity, updates } });
            return true;
        }
        try {
            const { data: _opportunity } = await axios.put(`/api/opportunities/${opportunity.id}?accesstoken=${accesstoken}&organizationId=${organization.id}`, { opportunity: { updates } });
            onUpdateOpportunity({ opportunity: _opportunity });
            return true;
        } catch(e) {
            setError(true);
            return false;
        }
    }


    function onCancel() {
        sessionStorage.removeItem(draftKey);
        setShowRichText(false);
    }

    function onDeleteComment(comment) {
        setDeleting(comment);
    }

    function onDeleteConfirm(comment) {
        setUpdates({ updates: updates.filter(Boolean).map(u => u.created === comment.created && u.createdBy === comment.createdBy ? { ...u, deleted: true } : u )});
    }

    async function onEditSave(comment) {
        const html = editRteRef.current?.editor?.getHTML();
        if (html && html.length > 7) {
            await setUpdates({ updates: updates.filter(Boolean).map(u => u.created === comment.created && u.createdBy === comment.createdBy ? { ...u, html, edited: new Date().getTime() } : u )});
        }
        setEditing(null);
    }

    return (
        <Box sx={{ width: '100%', background: theme.palette.background.default, pb: 3, overflowY: 'auto' }}>
            <Box sx={{ position: 'sticky', top: 0, background: 'transparent', padding: '6px 24px', mb: 3, borderBottom: '1px solid rgba(59, 130, 246, 0.3)', boxShadow: 'none' }}>
                <Typography variant='body1' sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#334155' }}>
                    Updates & Comments (internal use only)
                </Typography>
            </Box>
            
            {!opportunity && (
                <Box sx={{ marginTop: '36px', padding: '24px', maxWidth: 'calc(100% - 48px)', width: 'calc(100% - 48px)', margin: 'auto', padding: '24px 12px', border: `1px solid ${theme.palette.panel1}`, borderRadius: '6px', background: theme.palette.panel1 }}>
                    <Alert severity='warning'>
                        This opportunity is not yet saved. To add comments and files here, first save the opportunity.
                    </Alert>
                </Box>
            )}

            {opportunity && (
                <Box sx={{ marginTop: '36px', padding: '24px', maxWidth: 'calc(100% - 48px)', width: 'calc(100% - 48px)', margin: 'auto', padding: '24px 0px', background: theme.palette.background.default }}>

                    {!showRichText && (
                        <TextField
                            fullWidth
                            placeholder={'Do you have any updates to add?'}
                            onMouseDown={() => {
                                setShowRichText(true);
                                const func = () => {
                                    if (rteRef.current?.editor) {
                                        rteRef.current?.editor?.chain().focus().run();
                                    }
                                    else {
                                        setTimeout(func, 10)
                                    }
                                };
                                func();
                            }}
                        />
                    )}

                    {showRichText && (
                        <RichTextEditor
                            ref={rteRef}
                            extensions={[
                                StarterKit,
                                Mention.configure({
                                    suggestion: TipTapMentionSuggestion({ items: mentionItemsFromOrg({ organization }) }),
                                }),
                            ]}
                            content={savedDraft || ''}
                            onUpdate={({ editor }) => {
                                const html = editor.getHTML();
                                if (html && html.length > 7) {
                                    sessionStorage.setItem(draftKey, html);
                                } else {
                                    sessionStorage.removeItem(draftKey);
                                }
                            }}
                            renderControls={() => (
                                <MenuControlsContainer>
                                    <MenuSelectHeading />
                                    <MenuDivider />
                                    <MenuButtonBold />
                                    <MenuButtonItalic />
                                </MenuControlsContainer>
                            )}
                        />
                    )}

                    {showRichText && files.length > 0 && (
                        <Box sx={{ display: 'flex', marginTop: '12px' }}>
                            {files.map(file => (
                                <Box sx={{ display: 'flex', alignItems: 'center', margin: '0px 6px', position: 'relative' }} key={file.location} >
                                    <a href={file.url} target={'_blank'} style={{ textAlign: 'center', cursor: 'pointer', textDecoration: 'none', color: '#000' }}>
                                        <FontAwesomeIcon icon={fileToIcon(file.name)} style={{ fontSize: '48px', marginBottom: '6px' }} />
                                        <Typography variant='caption' component={Box}>
                                            {file.name.substr(0, 20)}{file.name.length > 20 ? '...' : ''}
                                        </Typography>
                                    </a>
                                    <IconButton
                                        color='error'
                                        sx={{ position: 'absolute', top: '-3px', right: '-3px' }}
                                        onClick={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setFiles(files.filter(f => f !== file))
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faXmark} />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {showRichText && (
                        <Box sx={{ marginTop: '12px', padding: '12px', textAlign: 'center', border: `1px solid ${theme.palette.border1}`, boxShadow: '1px 1px 1px rgba(0,0,0,0.4)', cursor: 'pointer' }}>
                            {file === null && (
                                <div {...getRootProps()}>
                                    <FontAwesomeIcon style={{ fontSize: '48px' }} icon={faCloudArrowUp} />
                                    <input {...getInputProps()} />
                                    <Typography variant='caption' component={Box} >Drag and drop files here or click to browse.</Typography>
                                </div>
                            )}
                            {file !== null && file.state === 'uploading' && (
                                <Box sx={{ textAlign: 'center' }}>
                                    <CircularProgress size={18} />
                                </Box>
                            )}
                        </Box>
                    )}

                    {showRichText && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px' }}>
                            <Button variant='contained' color='primary' sx={{ flex: 1, marginTop: '12px' }} onClick={onPosting ? () => { } : onPost}>
                                {!onPosting ? 'Post' : <CircularProgress size={24} />}
                            </Button>
                            <IconButton color='error' sx={{ flex: 0, marginTop: '12px', marginLeft: '6px' }} disabled={onPosting} onClick={onCancel}>
                                <FontAwesomeIcon icon={faXmark} />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            )}

            {error && (
                <Alert severity='error' sx={{ marginTop: '12px' }}>
                    <Typography variant='caption'>Error saving your comment, please try again later.</Typography>
                </Alert>
            )}

            {/* Status log entries merged with comments */}
            {(() => {
                const ACTION_LABELS = {
                    'sent-to-endcustomer': 'Quote sent to customer',
                    'sending-to-endcustomer': 'Sending quote to customer',
                    'marked-completed': 'Marked as Won',
                    'marked-won': 'Marked as Won',
                    'marked-lost': 'Marked as Lost',
                    'accepted': 'Accepted by customer',
                    'rejected': 'Rejected by customer',
                    'invalidate-link': 'Reopened — customer link invalidated',
                    'follow-up-sent': 'Follow-up sent',
                    'deleted': 'Quote deleted',
                };
                const parseReason = r => { if (!r) return null; if (typeof r === 'object') return r; try { return JSON.parse(r); } catch (e) { return null; } };
                const statusEntries = (opportunity.log || [])
                    .filter(l => ACTION_LABELS[l.action])
                    .map(l => {
                        // For "Quote sent to customer", show who it went to: the recipient email(s)
                        // when emailed, or note that it was shared via a generated link (no recipients).
                        let sentDetail = null;
                        if (l.action === 'sent-to-endcustomer') {
                            const addresses = (parseReason(l.reason)?.toRecipients || []).map(r => r?.address).filter(Boolean);
                            sentDetail = addresses.length > 0 ? addresses.join(', ') : 'sent via generated link';
                        }
                        // "Marked as Lost" shows the reason picked in the Lost dialog.
                        if (l.action === 'marked-lost' && l.lostReason) sentDetail = lostReasonText(l.lostReason);
                        return { _type: 'status', created: l.timestamp, label: l.action === 'follow-up-sent' && l.recipients ? `Follow-up sent to ${l.recipients}` : ACTION_LABELS[l.action], sentDetail, modifiedBy: l.modifiedBy, customerName: l.customerName };
                    });
                // Some comments are really events: the note written when a quote is duplicated, and a
                // follow-up logged as done outside Monada. Show them like the status log entries.
                const isEventComment = u => u.followUp || /^\s*Created by duplicating opportunity/.test(u.html || '');
                const liveUpdates = (updates || []).filter(u => u && !u.deleted);
                const comments = liveUpdates.filter(u => !isEventComment(u)).map(u => ({ ...u, _type: 'comment' }));
                const eventComments = liveUpdates.filter(isEventComment).map(u => ({ _type: 'status', created: u.created, labelHtml: u.html, modifiedBy: u.createdBy }));
                return [...comments, ...statusEntries, ...eventComments].sort((a, b) => b.created - a.created);
            })().map(entry => entry._type === 'status' ? (
                <Box key={`log-${entry.created}`} sx={{ maxWidth: 'calc(100% - 48px)', width: 'calc(100% - 48px)', margin: 'auto', padding: '10px 12px', borderLeft: `3px solid ${theme.palette.primary.main}`, background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc', borderRadius: '0 6px 6px 0', marginTop: '12px' }}>
                    {entry.labelHtml ? (
                        <Typography component='div' sx={{ fontSize: '12px', fontWeight: 600, color: theme.palette.text.primary, '& p': { margin: 0 }, '& strong': { fontWeight: 600 }, '& a': { color: theme.palette.primary.main } }}
                            dangerouslySetInnerHTML={{ __html: entry.labelHtml }} />
                    ) : (
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: theme.palette.text.primary }}>
                            {entry.label}{entry.sentDetail ? ` — ${entry.sentDetail}` : ''}{entry.customerName ? ` — ${entry.customerName}` : ''}
                        </Typography>
                    )}
                    <Typography variant='caption' sx={{ color: theme.palette.text.secondary }}>
                        {dayjs(entry.created).format('MMM D, YYYY [at] h:mm A')}{entry.modifiedBy ? ` · ${entry.modifiedBy.split('@')[0]}` : ''}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ maxWidth: 'calc(100% - 48px)', width: 'calc(100% - 48px)', margin: 'auto', padding: '24px 12px', border: `1px solid ${theme.palette.border1}`, borderRadius: '6px', background: theme.palette.panel1, marginTop: '12px' }} id={entry.created} key={entry.created}>
                    <Box sx={{ display: 'flex' }}>
                        <Typography variant='caption' component={Box} sx={{ color: theme.palette.text.secondary, marginBottom: '12px', flex: 1 }}>
                            {entry.createdBy} • {dayjs(entry.created).fromNow()}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: '4px' }}>
                            <IconButton size='small' onClick={() => setEditing(entry)} sx={{ color: theme.palette.text.disabled, '&:hover': { color: theme.palette.text.primary } }}>
                                <FontAwesomeIcon icon={faPen} style={{ fontSize: '13px' }} />
                            </IconButton>
                            <IconButton size='small' onClick={() => onDeleteComment(entry)} sx={{ color: theme.palette.text.disabled, '&:hover': { color: red[500] } }}>
                                <FontAwesomeIcon icon={faTrash} style={{ fontSize: '13px' }} />
                            </IconButton>
                        </Box>
                    </Box>
                    {!entry.deleted && editing?.created === entry.created && editing?.createdBy === entry.createdBy && (
                        <Box>
                            <RichTextEditor
                                ref={editRteRef}
                                extensions={[
                                    StarterKit,
                                    Mention.configure({
                                        suggestion: TipTapMentionSuggestion({ items: mentionItemsFromOrg({ organization }) }),
                                    }),
                                ]}
                                content={entry.html}
                                renderControls={() => (
                                    <MenuControlsContainer>
                                        <MenuSelectHeading />
                                        <MenuDivider />
                                        <MenuButtonBold />
                                        <MenuButtonItalic />
                                    </MenuControlsContainer>
                                )}
                            />
                            <Box sx={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                                <Button variant='contained' color='primary' size='small' onClick={() => onEditSave(entry)}>Save</Button>
                                <Button variant='outlined' size='small' onClick={() => setEditing(null)}>Cancel</Button>
                            </Box>
                        </Box>
                    )}
                    {!entry.deleted && !(editing?.created === entry.created && editing?.createdBy === entry.createdBy) && (
                        <>
                            <Box sx={{ fontSize: '12px', lineHeight: 1.5, '& p': { margin: '0 0 4px' } }} dangerouslySetInnerHTML={{ __html: renderMentions({ html: entry.html, me: me?.email, users: organization?.users }) }} />
                            {entry.edited && (
                                <Typography variant='caption' sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                                    (edited {dayjs(entry.edited).fromNow()})
                                </Typography>
                            )}
                            {entry.files?.length > 0 && (
                                <Box sx={{ display: 'flex', marginTop: '12px' }}>
                                    {entry.files.map(file => (
                                        <Box sx={{ display: 'flex', alignItems: 'center' }} key={file.url} >
                                            <a href={file.url} target={'_blank'} style={{ textAlign: 'center', cursor: 'pointer', textDecoration: 'none', color: '#000' }}>
                                                <FontAwesomeIcon icon={fileToIcon(file.name)} style={{ fontSize: '48px', marginBottom: '6px' }} />
                                                <Typography variant='caption' component={Box}>
                                                    {file.name.substr(0, 20)}{file.name.length > 20 ? '...' : ''}
                                                </Typography>
                                            </a>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                            {deleting?.created === entry.created && deleting?.createdBy === entry.createdBy && (
                                <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '8px', background: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontSize: '12px', color: '#64748b' }}>Delete this comment?</Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button size='small' variant='outlined' sx={{ fontSize: '11px', textTransform: 'none', borderColor: '#e2e8f0', color: '#64748b', '&:hover': { borderColor: '#cbd5e1' } }} onClick={() => setDeleting(null)}>Cancel</Button>
                                        <Button size='small' variant='contained' color='error' sx={{ fontSize: '11px', textTransform: 'none' }} onClick={() => onDeleteConfirm(entry)}>Delete</Button>
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            ))}
        </Box>
    );
}

function fileToIcon(filename) {
    return {
        'jpg': faFileJpg,
        'jpeg': faFileJpg,
        'pdf': faFilePdf,
        'doc': faFileWord,
        'docx': faFileWord,
        'xls': faFileExcel,
        'xlsx': faFileExcel,
        'ppt': faFilePowerpoint,
        'pptx': faFilePowerpoint,
    }[filename.split('.').pop().toLowerCase()] || faFile;
}

export default SidePaneFilesAndComments;

